import React, { ReactElement } from 'react';
import { AlertService } from 'components/alertService';
import { getGraphQLError } from 'utils/error';
import { CustomLinkProps } from 'routes/types';
import { useAvigilonCamerasHandoff } from './useAvigilonCamerasHandoff';

/**
 * Sidebar link for the Avigilon "Cameras" handoff.
 *
 * Rendered in place of the default react-router `Link` for the `/cameras` route. Rather
 * than navigating to the `/cameras` interstitial (which then can't open a new tab from
 * an effect without tripping popup blockers), it asks `useAvigilonCamerasHandoff` to open
 * the tab synchronously inside the click and point it at the resolved url. See sc-17102.
 *
 * The `href` still targets `/cameras` so the link degrades gracefully (and behaves for
 * assistive tech / hover) — the click handler always `preventDefault`s the navigation.
 */
export function CamerasSidebarLink({
  to,
  onClick,
  children,
  ...rest
}: CustomLinkProps): ReactElement {
  const openHandoff = useAvigilonCamerasHandoff();
  // Guards against a double-tap opening two tabs / firing two mutations.
  const handoffInFlightRef = React.useRef(false);

  const href = typeof to === 'string' ? to : '/cameras';

  return (
    <a
      {...rest}
      href={href}
      onClick={(event): void => {
        event.preventDefault();
        // Close the mobile sidebar drawer, when a handler was supplied.
        onClick?.(event);

        if (handoffInFlightRef.current) {
          return;
        }
        handoffInFlightRef.current = true;

        openHandoff({ openInNewTab: true })
          .catch((error) => {
            // The blank tab is already closed by the hook, so surface the failure as a
            // toast — otherwise the user just sees the placeholder tab flash and vanish.
            AlertService.alert(
              'danger',
              'Something went wrong!',
              getGraphQLError(error),
            );
          })
          .finally(() => {
            handoffInFlightRef.current = false;
          });
      }}
    >
      {children}
    </a>
  );
}
